/**
 * @file activitiesStep.component.tsx
 * @path apps/website/src/features/booking/components/steps/activitiesStep.component.tsx
 */

import { Activity } from "@sirbastians/types";
import { Badge, Button, Card, CardContent } from "@sirbastians/ui";
import { cn, formatCurrency } from "@sirbastians/utils";
import { Check, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useBookingStore } from "../booking.store";
import { activitiesData } from "@/data/activities";

export const ActivitiesStep = () => {
  const {
    selectedService,
    includedActivities,
    selectedActivities,
    toggleActivity,
  } = useBookingStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("All");

  // Available optional activities: prefer service-specific list, else all active
  const availableOptional = useMemo<Activity[]>(() => {
    if (
      selectedService?.optionalActivities &&
      selectedService.optionalActivities.length > 0
    ) {
      return selectedService.optionalActivities;
    }
    return activitiesData.filter(
      (a) =>
        a.status === "active" &&
        !includedActivities.some((inc) => inc.id === a.id),
    );
  }, [selectedService, includedActivities]);

  const categories = useMemo(
    () => ["All", ...new Set(availableOptional.map((a) => a.category))],
    [availableOptional],
  );

  const filtered = useMemo(
    () =>
      availableOptional.filter((a) => {
        const matchSearch =
          !searchQuery ||
          a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          a.shortDescription?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchCat =
          filterCategory === "All" || a.category === filterCategory;
        return matchSearch && matchCat;
      }),
    [availableOptional, searchQuery, filterCategory],
  );

  const isSelected = (a: Activity) =>
    selectedActivities.some((s) => s.id === a.id);
  const isIncluded = (a: Activity) =>
    includedActivities.some((inc) => inc.id === a.id);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Add Activities</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Enhance your trip with optional add-on experiences. This step is
          optional.
        </p>
      </div>

      {/* Included activities */}
      {includedActivities.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Included in your package
          </h3>
          <div className="flex flex-wrap gap-2">
            {includedActivities.map((a) => (
              <Badge
                key={a.id}
                variant="secondary"
                className="gap-1.5 text-xs py-1 px-2.5"
              >
                <Check className="h-3 w-3 text-accent" />
                {a.title}
              </Badge>
            ))}
          </div>
        </section>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="search"
          placeholder="Search activities…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-accent/50"
        />
      </div>

      {/* Category filters */}
      <div className="flex gap-2 flex-wrap">
        {categories.map((cat) => (
          <Button
            key={cat}
            variant={filterCategory === cat ? "primary" : "outline"}
            size="sm"
            onClick={() => setFilterCategory(cat)}
          >
            {cat}
          </Button>
        ))}
      </div>

      {/* Activity grid */}
      <div className="grid gap-4 sm:grid-cols-2">
        {filtered.map((activity) => {
          const selected = isSelected(activity);
          const included = isIncluded(activity);
          return (
            <ActivityCard
              key={activity.id}
              activity={activity}
              isSelected={selected}
              isIncluded={included}
              onToggle={() => !included && toggleActivity(activity)}
            />
          );
        })}
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">
          No activities match your search.
        </p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ActivityCard
// ---------------------------------------------------------------------------

interface ActivityCardProps {
  activity: Activity;
  isSelected: boolean;
  isIncluded: boolean;
  onToggle: () => void;
}

const ActivityCard = ({
  activity,
  isSelected,
  isIncluded,
  onToggle,
}: ActivityCardProps) => (
  <Card
    role="button"
    tabIndex={isIncluded ? -1 : 0}
    onClick={onToggle}
    onKeyDown={(e) =>
      !isIncluded && (e.key === "Enter" || e.key === " ") && onToggle()
    }
    aria-pressed={isSelected || isIncluded}
    className={cn(
      "cursor-pointer transition-all duration-200 overflow-hidden",
      isSelected && "border-accent ring-1 ring-accent/30 bg-accent/5",
      isIncluded && "border-accent/50 bg-accent/5 cursor-default opacity-80",
      !isSelected &&
        !isIncluded &&
        "hover:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/50",
    )}
  >
    {activity.gallery.primary && (
      <div className="h-32 overflow-hidden">
        <img
          src={activity.gallery.primary}
          alt={activity.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    )}

    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm leading-tight">
            {activity.title}
          </h3>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {activity.shortDescription}
          </p>
        </div>

        {/* Indicator */}
        <span
          className={cn(
            "shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors",
            isSelected || isIncluded
              ? "border-accent bg-accent"
              : "border-border",
          )}
        >
          {(isSelected || isIncluded) && (
            <Check className="h-3 w-3 text-white stroke-[3]" />
          )}
        </span>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-muted-foreground">
          {activity.duration}
        </span>
        {isIncluded ? (
          <Badge variant="secondary" className="text-[10px]">
            Included
          </Badge>
        ) : activity.pricing?.amount > 0 ? (
          <span className="text-xs font-semibold text-accent">
            +{formatCurrency(activity.pricing.amount)} / person
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Free</span>
        )}
      </div>
    </CardContent>
  </Card>
);
